import { FC, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, styled, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { BigNumber } from 'ethers';
import moment from 'moment';

import { useAppSelector } from 'state/hooks';
import { icons } from 'config/constants/assets';
import { ConnectWallet, ContainedButton } from 'components/common/Button';
// import { StakingModal } from 'components/Modal';
import { getBalanceInEther, getBalanceInWei, toTwoDigits } from 'utils';
// import { useStakingPool } from 'hooks/useStakingPool';
// import {useClaimRewardTimerBoardroom, useWithdrawTimerStaking} from 'hooks/useStakingPoolLockInfo';
import { useNotification } from 'hooks/useNotification';
import { ContainedInput } from 'components/common/Input';
import { useWeb3React } from '@web3-react/core';
import { useStake, useTokenPrice } from 'hooks/useStakeInfo';

const Container = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
//   borderRadius: '20px',
  background: "transparent",

  [theme.breakpoints.up('xs')]: {
    padding: '0px 16px',
  },
  [theme.breakpoints.up('md')]: {
    padding: '0px 32px',
  },
}));

// origin section
const Origin = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '0px 15px',
  padding: '16px 0px',

  [theme.breakpoints.up('xs')]: {
    gap: '0px 15px',
  },
  [theme.breakpoints.up('md')]: {
    gap: '15px 15px',
  },
}));

// token img section
const TokenImgSection = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '15px',
}));

const TokenImg = styled('img')(({ theme }) => ({
  width: '60px',

  [theme.breakpoints.up('xs')]: {
    width: '50px',
  },
  [theme.breakpoints.up('md')]: {
    width: '60px',
  },
}));

const PoolName = styled(Typography)(({ theme }) => ({
  fontFamily: 'Prompt',
  fontStyle: 'normal',
  fontWeight: '700',
  cursor: 'pointer',
  fontSize: '16px',
  lineHeight: '24px',
  maxWidth: '100px',
  whiteSpace: 'break-spaces',
  textAlign: 'center',

  [theme.breakpoints.up('xs')]: {
    display: 'block',
    margin: 'auto',
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  [theme.breakpoints.up('sm')]: {
    whiteSpace: 'break-spaces',
  },
  [theme.breakpoints.up('md')]: {
    display: 'block',
    textAlign: 'start',
    whiteSpace: 'break-spaces',
  },
}));

// info section
const InfoSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '30px',
  position: 'relative',
  textAlign: 'center',
  alignItems: 'center',

  [theme.breakpoints.up('xs')]: {
    gap: '15px',
    width: '100%',
    justifyContent: 'center',
  },
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
  },
  [theme.breakpoints.up('md')]: {
    gap: '30px',
  },
}));

const InfoCell = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'end',
  fontFamily: 'Prompt',
  fontStyle: 'normal',
  fontWeight: '400',
  cursor: 'pointer',
  fontSize: '14px',
  lineHeight: '21px',
  color: 'rgba(255, 255, 255, 0.7)',
  margin: 'auto',

  img: {
    width: '20px',
    marginRight: '8px',
  },

  [theme.breakpoints.up('xs')]: {
    fontSize: '12px',
    lineHeight: '18px',
    img: {
      display: 'none',
    },
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '14px',
    lineHeight: '21px',
    img: {
      display: 'block',
    },
  },
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter',
  fontStyle: 'normal',
  fontWeight: '600',
  cursor: 'pointer',
  fontSize: '25px',
  lineHeight: '30px',
  color: '#ffffff',
  textAlign: 'start',
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'end',
  margin: 'auto',

  span: {
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: '400',
    marginLeft: '4px',
  },

  [theme.breakpoints.up('xs')]: {
    fontSize: '19px',
    lineHeight: '23px',
    span: {
      fontSize: '10px',
      lineHeight: '14px',
    },
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '20px',
    lineHeight: '24px',
    span: {
      fontSize: '12px',
      lineHeight: '16px',
    },
  },
}));

const RewardImg = styled('img')(() => ({
  width: '22px',
}));

// desktop expanded section
const DesktopExpanded = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-around',
  flexWrap: 'wrap',
  gap: '18px',
  alignItems: 'center',
  padding: '0px 0px 16px',

  [theme.breakpoints.up('xs')]: {
    border: 'none',
    display: 'none',
  },
  [theme.breakpoints.up('sm')]: {},
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

// global info section
const GlobalInfoSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '0px 15px',
  position: 'relative',
  textAlign: 'center',
  alignItems: 'center',
  width: '100%',
  borderTop: '1.5px solid rgba(255, 255, 255, 0.3)',
  borderBottom: '1.5px solid rgba(255, 255, 255, 0.3)',
  flexWrap: 'wrap',

  [theme.breakpoints.up('xs')]: {
    borderTop: 'none',
  },
  [theme.breakpoints.up('sm')]: {
    borderTop: '1.5px solid rgba(255, 255, 255, 0.3)',
  },
  [theme.breakpoints.up('md')]: {},
}));

const GlobalInfoCard = styled(Box)(({ theme }) => ({
  borderRadius: '10px',
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',

  [theme.breakpoints.up('xs')]: {
    padding: '10px 0px',
  },
  [theme.breakpoints.up('md')]: {
    padding: '15px 0px',
  },
}));

const GlobalInfoCardLabel = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontFamily: 'Prompt',
  fontStyle: 'normal',
  fontWeight: '600',
  width: '100%',
  cursor: 'pointer',
  textWrap: 'wrap',
  color: 'white',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'normal',
  [theme.breakpoints.up('xs')]: {
    fontSize: '12px',
    lineHeight: '18px',
    img: {
      display: 'none',
    },
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '14px',
    lineHeight: '18px',
    img: {
      display: 'block',
    },
  },
}));

const GlobalInfoCardValue = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  fontFamily: 'Inter',
  fontStyle: 'normal',
  fontWeight: '700',
  cursor: 'pointer',
  color: '#ffffff',

  [theme.breakpoints.up('xs')]: {
    fontSize: '12px',
    lineHeight: '15px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '12px',
    lineHeight: '15px',
  },
}));

// action section
const ActionSection = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  position: 'relative',
  textAlign: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  gap: '10px',
}));

const ActionInfoSection = styled(Box)(() => ({
  maxWidth: '130px',
  margin: 'auto',
  marginBottom: '6px',
  minWidth: '130px'
}));

const ActionInfo1 = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontFamily: 'Prompt',
  fontStyle: 'normal',
  fontWeight: '400',
  whiteSpace: 'break-spaces',
  color: 'rgba(255, 255, 255, 0.7)',
  justifyContent: 'center',

  [theme.breakpoints.up('xs')]: {
    fontSize: '12px',
    lineHeight: '18px',
  },
}));

const ActionInfo2 = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontFamily: 'Prompt',
  fontStyle: 'normal',
  fontWeight: '700',
  whiteSpace: 'break-spaces',
  color: 'rgba(255, 255, 255, 255)',
  justifyContent: 'center',

  [theme.breakpoints.up('xs')]: {
    fontSize: '12px',
    lineHeight: '15px',
  },
}));

const ButtonSection = styled(Box)(() => ({}));

const ActionButton = styled(ContainedButton)(() => ({
  maxWidth: '102px',
  height: '30px',
}));

// mobile expanded section
const MobileExpanded = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-around',
  flexWrap: 'wrap',
  gap: '18px',
  alignItems: 'center',
  padding: '0px 0px 12px',

  [theme.breakpoints.up('xs')]: {
    border: 'none',
    display: 'flex',
    borderTop: '2px solid #CCC292',
  },
  [theme.breakpoints.up('sm')]: {
    border: 'none',
  },
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

// global info section
const MobileGlobalInfoCard = styled(Box)(() => ({
  textAlign: 'center',
}));

const MobileGlobalInfoCardLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Prompt',
  fontStyle: 'normal',
  fontWeight: '400',
  cursor: 'pointer',
  color: 'rgba(255, 255, 255, 0.7)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: '12px',
  lineHeight: 'normal',
  maxWidth: '150px',
  whiteSpace: 'break-spaces',

  [theme.breakpoints.up('xs')]: {},
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const MobileGlobalInfoCardValue = styled(Typography)(() => ({
  fontFamily: 'Prompt',
  fontStyle: 'normal',
  fontWeight: '700',
  cursor: 'pointer',
  color: '#ffffff',
  fontSize: '12px',
  lineHeight: 'normal',
}));

const AmountInput = styled(ContainedInput)(() => ({
  height: '30px',
  maxWidth: '150px',
  marginRight: '5px',
  marginLeft: '5px'
}));

// action section
const MobileButtonSection = styled(Box)(() => ({
  textAlign: 'center',
}));

const MobileActionButton = styled(ContainedButton)(() => ({
  maxWidth: '102px',
  height: '40px',
}));

interface PoolCardProps {
  pool: any;
  type: string;
  info?: any;
  onUpdate: () => void
  isClaimable?: boolean
}

const TabPanel: FC<PoolCardProps> = ({ pool, type, info, onUpdate, isClaimable }) => {
  const [isExpanded, setExpanded] = useState<boolean>(!!true);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string | undefined>();
  const [isPending, setPending] = useState<boolean>(false);
  const [nextEpochCountDown, setNextEpochCountDown] = useState<string>('');
  const [claimTimeout, setClaimTimeout] = useState('');
  const [unstakeTimeout, setUnstakeTimeout] = useState('');

//   const { onClaim, onKickPrinter } = useStakingPool(pool);
//   const { data: tokenList } = useAppSelector((state) => state.tokens);
  const { onShowNotification } = useNotification();
  const { pendingTxHash } = useAppSelector((state) => state.modal);
  const pendingTxHashRef: { current: string | undefined } = useRef();
  pendingTxHashRef.current = pendingTxHash;
  const [amount, setAmount] = useState<string>('');
  const [amountInWei, setAmountInWei] = useState<BigNumber>(BigNumber.from(0));
  const { account } = useWeb3React();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [balance, setBalance] = useState<number>(0);
  const [allowance, setAllowance] = useState<number>(0);
  const [allowanceWei, setAllowanceInWei] = useState<BigNumber>(BigNumber.from(0));
  const { selectedChainId } = useAppSelector((state) => state.chain);

  const {onApprove, onStake, onUnstake} = useStake(Number(selectedChainId), amount, pool, info)
  const isApproved = account ? allowanceWei.gte(amountInWei) : false

  const {
    stakingToken,
    rewardToken,
    stakingTokenName,
    rewardTokenName,
    userInfo,
    nextEpochStartTimestamp,
    rewardPerShare,
    currentEpoch,
    period,
    name
  } = pool;
  const rewardBalance = Number(
    Number(userInfo?.rewardTokenBalance || BigNumber.from(0)) / 10 ** (userInfo?.rewardTokenDecimal || 18)
  );
  const stakedBalance = getBalanceInEther(userInfo?.stakedBalance || BigNumber.from(0));
  const canWithdraw = !!userInfo?.canWithdraw && stakedBalance > 0;
  const canClaimReward = !!userInfo?.canClaimReward;

  useEffect(() => {
    if( info  ){
      if( type === "Stake" ){
        setBalance(getBalanceInEther(info.accountBalance))
        setAllowance(getBalanceInEther(info.allowance));
        setAllowanceInWei(info.allowance)
      }
      if( type === "Unstake"){
        setBalance(getBalanceInEther(info.userStaked))
      }
    }
  }, [info]);

    const getButtonText = () => {
        if( type === 'Stake' ){
            if( !isApproved ) {
                return "Approve";
            }
        }
        if( type === 'Unstake' && isClaimable ){
          return "Unstake & Claim"
        }
        return type;
    };
  const onConfirm = async () => {
    setLoading(true);

    try {
        if( type === 'Stake'){
            if( !isApproved ){
                setLoading(true)
                await onApprove()
                onUpdate()
            } else {
                setLoading(true)
                await onStake(amountInWei.toString())
                onUpdate()
            }
        }
        if( type === 'Unstake' ){
            setLoading(true)
            await onUnstake(amountInWei.toString())
            onUpdate()
        }
        if( type === 'Claim' ){
            setLoading(true)
            await onUnstake("0")
            onUpdate()
        }
      } catch (err) {
        console.log('Presale err: ', err);
      }
      setLoading(false);
  };

  const onMax = async () => {
    setAmount(balance?.toString())
    setAmountInWei(getBalanceInWei(Number(balance).toString() || '0'));

  };

  useEffect(() => {
    const interval = setInterval(() => {
      const timeDiff = moment.duration(moment(1000 * (nextEpochStartTimestamp || 0)).diff(moment()));
      const hourDiff = toTwoDigits(timeDiff.hours());
      const minDiff = toTwoDigits(timeDiff.minutes());
      const secDiff = toTwoDigits(timeDiff.seconds());

      setNextEpochCountDown(`${hourDiff}:${minDiff}:${secDiff}`);
    }, 1000);

    return () => clearInterval(interval);
  }, [nextEpochStartTimestamp]);


  const onToggle = () => {
    setExpanded(!isExpanded);
  };

  const onKick = async () => {
    // try {
    //   await onKickPrinter();

    //   onShowNotification({
    //     title: 'Transaction Success',
    //     description: 'Kick',
    //     hasView: true,
    //     txHash: pendingTxHashRef.current,
    //   });
    // } catch (err) {
    //   onShowNotification({ title: 'Transaction Failed', description: 'Kick' });
    //   console.log('Kick Error: ', err);
    // }
  };

  const onDeposit = () => {
    setModalType('DEPOSIT');
    setModalOpen(true);
  };

  const onWithdraw = () => {
    setModalType('WITHDRAW');
    setModalOpen(true);
  };

  function isNumeric(n: any) {
    return !Number.isNaN(Number(n)) && Number.isFinite(Number(n));
  }

  const onChangeAmount = (e: any) => {
    if (!isNumeric(e.target.value)) return;
    const newAmount = e.target.value;
    setAmount(newAmount);
    setAmountInWei(getBalanceInWei(Number(newAmount).toString() || '0'));
  };
  const onClaimReward = async () => {
    setPending(true);

    //try {
    //   await onClaim();
    //   onShowNotification({
    //     title: 'Transaction Success',
    //     description: 'Claim Reward',
    //     hasView: true,
    //     txHash: pendingTxHashRef.current,
    //   });
    // } catch (err) {
    //   onShowNotification({ title: 'Transaction Failed', description: 'Claim Reward' });
    //   console.log('Claim Error: ', err);
    // }
    // setPending(false);
  };
  const getNoteText = () => {
    if( type === 'Stake')
        {
            return "After staking is initiated timer will be set to 72 hours. In order to claim your rewards with no penalty to initial staked amount , timer needs to hit 0."
        }
    else if (type === 'Unstake') {
        return "If you want to initiate Unstake while Timer is not expired (72 hours have not passed since you staked) you will lose 10% of the amount that is being unstaked and all accumulated rewards are reset to 0."
    }
    else if( type === 'Claim' ){
        return "Claim can be initiated only when Timer is expired. After rewards are claimed, Timer will be reset to 72 hours."
    }
    return type;
};
const getNoteText2 = () => {
    if( type === 'Stake')
        {
            return "Staking more LP tokens on top of existing staked amount will reset the timer to 72 hours."
        }
    if( type === 'Claim' ){
        return "Please use UNSTAKE & CLAIM button if you want to withdraw initial staked LP tokens with accumulated rewards."
    }
    return "";
};
const getNoteText3 = () => {
  if( type === 'Stake') {
    return "If staking more LP tokens while timer didnt expire, your accrued rewards go to 0."
  }
  if( type === 'Claim' ){
      return "If you initiate claim transaction before timer hits 0, you will receive 0 rewards."
  }
  return "";
};
  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };


  return (
    <>
      {/* {modalOpen && modalType && <StakingModal info={pool} modalType={modalType} onClose={onCloseModal} />} */}

      <Container>
        {/*  */}
        <Box display='flex' flexDirection='row'>
            { type !== 'Claim' && 
                <AmountInput onChange={onChangeAmount}  onClickMax={() => {}} value={amount} />
            }
            {account ? (
            <> 
                { type !== 'Claim' && 
                    <ActionButton variant='outlined'  disabled={false} onClick={onMax} sx={{ maxWidth: '40px' }}>
                        Max
                    </ActionButton>
                }
                    <ActionButton variant='outlined'  disabled={ type === 'Claim' ? !isClaimable: false} onClick={onConfirm} sx={{ maxWidth: '152px', marginLeft: '5px' }}>
                            {isLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : getButtonText()}
                    </ActionButton>
            </>

            ) : (
            <ConnectWallet  width={150} />
            )}
        </Box>
        <Box display='flex' sx={{width: '100%'}} flexDirection='column' justifyContent='center' alignItems='center'>
            <GlobalInfoCard>
                <GlobalInfoCardLabel>{`*${getNoteText()}`}</GlobalInfoCardLabel>
                <GlobalInfoCardLabel sx={{display: getNoteText2().length === 0 ? 'none' : ''}}>{`*${getNoteText2()}`}</GlobalInfoCardLabel>
                <GlobalInfoCardLabel sx={{display: getNoteText3().length === 0 ? 'none' : ''}}>{`*${getNoteText3()}`}</GlobalInfoCardLabel>
            </GlobalInfoCard>
        </Box>
      </Container>
    </>
  );
};

export default TabPanel;
